import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, IconButton, Paper, Tab, Tabs, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { deleteDepartment, getDepartments, getDepartmentTree } from '../../../../api/endpoints/corporate';
import type { Department, DepartmentTreeNode } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  DataTable,
  EmptyState,
  PageHeader,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { DepartmentFormDialog } from '../DepartmentFormDialog';
import { DepartmentReparentDialog } from '../DepartmentReparentDialog';
import { DepartmentOrgChartView } from '../OrgChartView';

interface FlatRow {
  row: Department;
  depth: number;
}

/** Flattens the parent-linked list into a depth-annotated, DFS-ordered array so it can be
 *  rendered as an indented plain Table (no @mui/x-data-grid / tree-view dependency — see
 *  other modules for the TS 4.9.5 incompatibility note on DataGrid). Orphaned rows are
 *  appended at depth 0 rather than silently dropped. */
function flattenTree(rows: Department[]): FlatRow[] {
  const byParent = new Map<number | null, Department[]>();
  for (const row of rows) {
    const key = row.parentDepartmentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const result: FlatRow[] = [];
  const visited = new Set<number>();

  function walk(parentId: number | null, depth: number) {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      result.push({ row: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);

  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      result.push({ row, depth: 0 });
    }
  }
  return result;
}

/** Self + all descendants of `rootId` — keeps the parent picker from letting a department
 *  become its own ancestor (a cycle). */
function collectDescendantIds(rows: Department[], rootId: number): Set<number> {
  const byParent = new Map<number | null, number[]>();
  for (const row of rows) {
    const key = row.parentDepartmentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row.id);
  }
  const result = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    for (const childId of byParent.get(id) ?? []) {
      if (!result.has(childId)) {
        result.add(childId);
        stack.push(childId);
      }
    }
  }
  return result;
}

/** Flatten the server-built `CorporateDepartment/GetTree` hierarchy back into the
 *  parent-linked `Department[]` shape the rest of this page consumes. */
function treeToRows(nodes: DepartmentTreeNode[], parentId: number | null = null): Department[] {
  const out: Department[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, isActive: node.isActive, parentDepartmentId: parentId });
    if (node.children?.length) out.push(...treeToRows(node.children, node.id));
  }
  return out;
}

export default function DepartmentListPage() {
  const [view, setView] = useState<'list' | 'chart'>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [reparenting, setReparenting] = useState<Department | null>(null);

  // Server tree primary, flat `GetDepartments` fallback when `GetTree` fails. Tree key nests
  // under `['corporate','departments']` so the dialogs' invalidation refreshes both.
  const treeQuery = useQuery({
    queryKey: ['corporate', 'departments', 'tree'],
    queryFn: () => getDepartmentTree(),
    retry: false,
  });
  const listQuery = useQuery({
    queryKey: ['corporate', 'departments'],
    queryFn: () => getDepartments(),
    enabled: treeQuery.isError,
  });
  const isError = treeQuery.isError && listQuery.isError;
  const isLoading =
    (treeQuery.isPending && !treeQuery.isError) || (treeQuery.isError && listQuery.isPending);

  const rows: Department[] = useMemo(
    () => (treeQuery.data?.tree ? treeToRows(treeQuery.data.tree) : listQuery.data?.departments ?? []),
    [treeQuery.data, listQuery.data]
  );

  const flattened = useMemo(() => flattenTree(rows), [rows]);
  const options = useMemo(
    () => flattened.map(({ row, depth }) => ({ id: row.id, label: `${'— '.repeat(depth)}${row.name}` })),
    [flattened]
  );
  const excludeIds = useMemo(
    () => (editing || reparenting ? collectDescendantIds(rows, (editing ?? reparenting)!.id) : new Set<number>()),
    [rows, editing, reparenting]
  );

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteDepartment(id),
    invalidateKeys: [['corporate', 'departments']],
    successMessage: 'Departman silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.name ?? '';

  const columns: DataTableColumn<FlatRow>[] = [
    {
      id: 'name',
      header: 'Departman Adı',
      cell: ({ row, depth }) => <Box sx={{ pl: depth * 3 }}>{row.name}</Box>,
    },
    { id: 'status', header: 'Durum', cell: ({ row }) => <StatusChip active={row.isActive} /> },
  ];

  return (
    <Box>
      <PageHeader
        title="Departmanlar"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Yeni Departman
          </Button>
        }
      />

      {isError && <ApiErrorAlert error={listQuery.error ?? treeQuery.error} sx={{ mb: 2 }} />}

      <Tabs value={view} onChange={(_e, value) => setView(value)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab value="list" label="Liste" sx={{ minHeight: 36 }} />
        <Tab value="chart" label="Organizasyon Şeması" sx={{ minHeight: 36 }} />
      </Tabs>

      {view === 'chart' ? (
        <Paper variant="outlined">
          <DepartmentOrgChartView rows={rows} onNodeClick={(department) => setEditing(department)} />
        </Paper>
      ) : (
        <DataTable
          columns={columns}
          rows={flattened}
          getRowKey={({ row }) => row.id}
          loading={isLoading}
          emptyState={
            <EmptyState variant="row" colSpan={3} title="Kayıtlı departman bulunamadı." />
          }
          rowActions={({ row }) => (
            <RowActions
              onEdit={() => setEditing(row)}
              onDelete={() => del.request(row.id)}
              leading={
                <Tooltip title="Üst Departmanı Değiştir">
                  <IconButton size="small" onClick={() => setReparenting(row)} aria-label="Taşı">
                    <DriveFileMoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          )}
        />
      )}

      <DepartmentFormDialog
        open={createOpen}
        department={null}
        options={options}
        excludeIds={new Set()}
        onClose={() => setCreateOpen(false)}
      />
      <DepartmentFormDialog
        open={!!editing}
        department={editing}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setEditing(null)}
      />
      <DepartmentReparentDialog
        open={!!reparenting}
        department={reparenting}
        options={options}
        excludeIds={excludeIds}
        onClose={() => setReparenting(null)}
      />

      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Departmanı sil"
        description={`"${deletingName}" departmanını silmek istediğinize emin misiniz?`}
      />
    </Box>
  );
}
